import React, { useState } from 'react';
import axios from 'axios';
import Button from '../Button';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../LoadingSpinner';

interface TennisLinkImportStepProps {
  teamId: number;
  onContinue: () => void;
}

const TennisLinkImportStep: React.FC<TennisLinkImportStepProps> = ({ teamId, onContinue }) => {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [skipped, setSkipped] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setImportResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`/teams/${teamId}/import-availability`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data);
      toast.success('TennisLink data imported successfully!');
    } catch (error: any) {
      console.error('Failed to import TennisLink data:', error);
      toast.error(error.response?.data?.detail || 'Failed to import TennisLink data');
    } finally {
      setUploading(false);
    }
  };

  const handleSkip = () => {
    setSkipped(true);
    onContinue();
  };

  const handleContinue = () => {
    onContinue();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Step 3: Import from TennisLink (Optional)</h2>

      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">What is TennisLink?</h3>
        <p className="text-sm text-blue-800 dark:text-blue-200">
          TennisLink is a league management platform used by USTA and other tennis organizations.
          You can export team data, player availability, and match schedules as Excel files from
          TennisLink and import them here to save time on manual data entry.
        </p>
      </div>

      {!importResult && !skipped && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Upload TennisLink Excel Export File
            </label>
            {file ? (
              <div className="mt-1 px-4 py-3 border-2 border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20 rounded-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <svg
                      className="h-8 w-8 text-green-600 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{file.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Remove file"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md hover:border-green-400 dark:hover:border-green-500 transition-colors">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-gray-600 dark:text-gray-400 justify-center">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white dark:bg-gray-700 rounded-md font-medium text-green-600 dark:text-green-400 hover:text-green-500 dark:hover:text-green-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500"
                    >
                      <span>Upload a file</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        accept=".xls,.xlsx"
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">XLS, XLSX up to 10MB</p>
                </div>
              </div>
            )}
          </div>

          {file && (
            <div className="flex justify-end">
              <Button onClick={handleUpload} disabled={uploading}>
                {uploading ? 'Importing...' : 'Import File'}
              </Button>
            </div>
          )}

          {uploading && (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Importing data...</span>
            </div>
          )}
        </div>
      )}

      {importResult && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
          <h3 className="text-sm font-semibold text-green-900 dark:text-green-300 mb-2">Import Successful!</h3>
          <div className="text-sm text-green-800 dark:text-green-200 space-y-1">
            {importResult.created_users !== undefined && (
              <p>
                {importResult.created_users} users created, {importResult.added_members} members
                added
              </p>
            )}
            {importResult.created_matches !== undefined && (
              <p>{importResult.created_matches} matches created</p>
            )}
            {importResult.updated_availability !== undefined && (
              <p>{importResult.updated_availability} availability records imported</p>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={handleSkip}>
          Skip for now
        </Button>
        <Button
          variant="primary"
          onClick={handleContinue}
          disabled={!importResult && !skipped && !file}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default TennisLinkImportStep;

